import { maskVendor } from "@/shared/lib/vendorMask";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { Card } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { AlertCircle, Download, ImageOff, Loader2, RefreshCw, Trash2 } from "lucide-react";
import type { PosterAssetDto } from "../../../api/posterService";

export function PosterAssetCard({
  asset,
  busy = false,
  onDownload,
  onRegen,
  onDelete,
  fitActualRatio = false,
  imageHeight,
  caption,
}: {
  asset: PosterAssetDto;
  /** v2.8 单图 regenerate 进行中:禁用 button + 转 loading icon。 */
  busy?: boolean;
  onDownload?: () => void;
  onRegen?: () => void;
  onDelete?: () => void;
  /**
   * 2026-07-28 — 按产物**真实**画幅渲染,而不是恒 3:4。
   *
   * ★这条是「多比例套图三张卡长得一模一样」的根治:老写法 `aspect-[3/4]` + `object-cover`,
   * 16:9 的横图被塞进竖框再居中裁掉两边,卡上标着 16:9 画面却是竖的 —— 标签和画面自相矛盾。
   * 没有真实像素(还在生成 / 老数据)时回落 3:4,行为与改前一致。
   */
  fitActualRatio?: boolean;
  /**
   * 图区固定高度(px)。给了它 → 宽度由 aspect-ratio 反算,一排卡**高度齐、宽度随比例变**。
   * ★不给的话就是固定宽度,竖图 235px 高、横图 74px 高,一排卡参差不齐像掉了牙。
   */
  imageHeight?: number;
  /** 卡底信息行的副标题(比例 / 爆点标题)。★不叠在图上 —— 压图的 badge 正是「显示重合」的来源。 */
  caption?: string;
}) {
  const { t } = useTranslation("poster");
  const failed = asset.status === "FAILED";
  const [imgBroken, setImgBroken] = useState(false);
  const previewUrl = asset.thumbUrl ?? null;
  const actualRatio =
    fitActualRatio && asset.actualWidth && asset.actualHeight
      ? `${asset.actualWidth} / ${asset.actualHeight}`
      : null;
  return (
    <Card
      className={cn("overflow-hidden", failed && "border-destructive ring-1 ring-destructive/40")}
    >
      <div
        className={cn("relative bg-muted", !actualRatio && !imageHeight && "aspect-[3/4]")}
        style={{
          ...(actualRatio ? { aspectRatio: actualRatio } : {}),
          ...(imageHeight ? { height: imageHeight } : {}),
        }}
      >
        {failed ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-destructive">
            <AlertCircle className="h-6 w-6" />
            <p className="text-center text-xs">
              {maskVendor(asset.failureReason) ?? t("asset.generationFailed")}
            </p>
          </div>
        ) : previewUrl && !imgBroken ? (
          <img
            src={previewUrl}
            alt={`seq ${asset.seq}`}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
            onError={() => setImgBroken(true)}
          />
        ) : previewUrl && imgBroken ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 px-3 text-muted-foreground">
            <ImageOff className="h-6 w-6 opacity-60" />
            <p className="text-center text-2xs leading-tight">{t("asset.imageLoadFailed")}</p>
            <button
              type="button"
              className="text-2xs text-primary underline-offset-2 hover:underline"
              onClick={() => setImgBroken(false)}
            >
              {t("asset.clickRetry")}
            </button>
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
            {t("asset.generating")}
          </div>
        )}
        {/* Phase D (2026-05-16) — GOOGLE provider Lanczos 上采样 4K 时标 badge,
            用户清楚知道这张是放大的不是原生 4K 出图。 */}
        {!failed && asset.isUpscaled && (
          <span
            className="absolute right-1 top-1 rounded bg-warning/85 px-1.5 py-0.5 text-2xs font-medium text-warning-foreground shadow"
            title="GOOGLE provider 2K Lanczos 放大至 4K"
          >
            上采样 4K
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-1 p-2">
        <span className="flex min-w-0 flex-col text-xs text-muted-foreground">
          <span>#{asset.seq}</span>
          {caption && (
            <span className="truncate text-2xs text-foreground/70" title={caption}>
              {caption}
            </span>
          )}
        </span>
        <div className="flex gap-1">
          {!failed && onDownload && (
            <Button variant="ghost" size="sm" onClick={onDownload}>
              <Download className="h-3 w-3" />
            </Button>
          )}
          {onRegen && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={onRegen}
              title={t("asset.regenTooltip")}
            >
              {busy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
            </Button>
          )}
          {onDelete && (
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
